// src/api/index.js
// Single API file — complete replacement always
// Change REACT_APP_API_URL in .env to switch provider

// Fallback = local backend port (5000 — PORT in the backend .env).
// Production always sets REACT_APP_API_URL.
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Doctor suspended pending admin approval — alert shown once (27 Sep 2026).
let suspensionNoticeShown = false;

// ─── Token management ─────────────────────────────────────────────────────
// sessionStorage (not localStorage) — clears automatically when the browser
// or tab closes, satisfying "closing browser should log the user out"
// without breaking normal page refreshes (sessionStorage survives those).
// NOTE: this makes sessions per-tab — logging in on one tab doesn't share
// the session with another tab open to the same site. That's the standard
// tradeoff of sessionStorage and is expected, not a bug.

function getToken() {
  return sessionStorage.getItem('thyro_access_token');
}

function setTokens(access, refresh) {
  sessionStorage.setItem('thyro_access_token', access);
  if (refresh) sessionStorage.setItem('thyro_refresh_token', refresh);
}

function clearTokens() {
  sessionStorage.removeItem('thyro_access_token');
  sessionStorage.removeItem('thyro_refresh_token');
  // The "Confirm it's you" box after registration belongs to these tokens;
  // never leave it behind once they're gone (audit 8 Oct 2026).
  sessionStorage.removeItem('thyro_confirm_password');
}

// ─── Token refresh (shared) ───────────────────────────────────────────────
// One refresh at a time (28 Sep 2026): the server rotates the refresh token
// on every use, so when several requests expire together (a dashboard
// loading photos, documents and data at once) they must all wait for ONE
// refresh — a second refresh with the already-used token would fail and
// log the user out. Resolves to the new access token, or null.
let refreshInFlight = null;
const REFRESH_TRANSIENT = 'REFRESH_TRANSIENT';
function refreshAccessToken(fetchFn) {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = sessionStorage.getItem('thyro_refresh_token');
      if (!refreshToken) return null;
      // A busy server, "too many requests" or a network blip is not a
      // logout (audit 9 Oct 2026): tried again once, and if it still fails
      // the request fails with a "try again" message instead of ending
      // the session. Only a refused refresh token logs out.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        let r;
        try {
          r = await fetchFn(`${BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });
        } catch (e) {
          r = null; // network problem
        }
        if (r && r.ok) {
          const { accessToken, refreshToken: newRefresh } = await r.json();
          setTokens(accessToken, newRefresh);
          return accessToken;
        }
        if (r && r.status !== 429 && r.status < 500) return null; // refused — the session has ended
        if (attempt === 0) await new Promise(res => setTimeout(res, 2000));
      }
      return REFRESH_TRANSIENT;
    })()
      .catch(() => REFRESH_TRANSIENT)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

// True when the server says the access token has expired — a 401, or the
// 400 { code: 'TOKEN_EXPIRED' } some endpoints send (see apiFetch below).
async function tokenExpired(response) {
  if (response.status === 401) return true;
  if (response.status !== 400) return false;
  try { return (await response.clone().json())?.code === 'TOKEN_EXPIRED'; } catch { return false; }
}

// ─── Core fetch wrapper ───────────────────────────────────────────────────

async function apiFetch(path, options = {}) {
  const token = getToken();
  // FormData bodies (uploadPhoto, uploadDocument) must NOT get an
  // explicit Content-Type — the browser sets multipart/form-data with
  // the correct boundary automatically, but ONLY if Content-Type is left
  // unset entirely. Forcing 'application/json' here (as this function
  // used to do unconditionally) sent multipart bytes under a JSON
  // Content-Type, which the backend then tried to JSON.parse and failed
  // with "Unexpected token '-', "------WebK"... is not valid JSON" —
  // every photo and document upload on the platform hit this, not just
  // one flow.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  // Hard timeout on every request. Without this, a hung connection (wrong
  // port, server not responding, network dropout) left the calling
  // component's loading state spinning forever — the fetch promise simply
  // never settled, so no .catch()/.finally() ever fired.
  // options.timeoutMs (24 Sep 2026): large multipart uploads — doctor
  // registration sends up to nine 5 MB documents — need longer than the
  // 20 s default on slower connections.
  const TIMEOUT_MS = options.timeoutMs || 20000;
  const fetchWithTimeout = (url, opts) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    return fetch(url, { ...opts, signal: controller.signal }).finally(() => clearTimeout(timer));
  };

  let response;
  try {
    response = await fetchWithTimeout(`${BASE_URL}${path}`, { ...options, headers });
  } catch (e) {
    const err = new Error(e.name === 'AbortError' ? 'Request timed out. Please check your connection and try again.' : 'Network error. Please check your connection and try again.');
    err.response = { status: 0, data: { error: err.message } };
    throw err;
  }

  // Auto-refresh on 401 — but only for requests that were actually
  // authenticated (i.e. we sent a token). A 401 on an unauthenticated call
  // like /auth/login just means "wrong credentials" and should bubble up
  // as a normal error for the caller to display — not trigger a session-
  // expiry redirect. Without this guard, every failed login attempt would
  // hard-navigate to /login (no refresh token exists yet on a fresh login),
  // wiping the form and the error message before it could ever be shown.
  //
  // BUG FIX: the backend signals an expired access token two different
  // ways depending on the endpoint — a conventional 401, or (seen on the
  // questionnaire autosave path) a 400 with { code: "TOKEN_EXPIRED" }.
  // Checking status === 401 alone missed the second shape entirely — a
  // request that failed this way never triggered a refresh, so once the
  // access token actually expired mid-session, every subsequent autosave
  // just kept failing for good (caught and swallowed by the caller — see
  // NoduleQuestionnaire.js etc.'s autosave catch block) instead of
  // refreshing and retrying, with the only visible sign being a console
  // error the patient would never see — a real data-loss risk on a long
  // questionnaire session. Peeking at the body via .clone() lets us check
  // for the 400/TOKEN_EXPIRED shape without consuming the response stream
  // the normal .json() read below still needs.
  // Login itself never refreshes and retries (10 Oct 2026) — a wrong
  // password is a 401 that must reach the login page as it is.
  if (token && path !== '/auth/login' && await tokenExpired(response)) {
    const accessToken = await refreshAccessToken(fetchWithTimeout);
    if (accessToken === REFRESH_TRANSIENT) {
      // keep the session and what is on screen; the caller shows this
      const e = new Error('Connection problem — please try again in a moment.');
      e.response = { status: 503, data: { error: e.message, message: e.message, code: 'TRY_AGAIN' } };
      throw e;
    }
    if (!accessToken) {
      clearTokens();
      window.location.href = '/login';
      return;
    }
    headers.Authorization = `Bearer ${accessToken}`;
    response = await fetchWithTimeout(`${BASE_URL}${path}`, { ...options, headers });
  }

  // Doctor suspended pending admin approval (27 Sep 2026): end the
  // session with an explanation instead of failing every request.
  if (token && response.status === 403) {
    let body403 = null;
    try { body403 = await response.clone().json(); } catch { /* not JSON */ }
    if (body403 && body403.code === 'PENDING_VERIFICATION') {
      clearTokens();
      sessionStorage.removeItem('thyro_user');
      // Show the message once only: a dashboard fires several requests at
      // the same moment, and the logout call itself is refused too — the
      // profile page has already explained the suspension in that case.
      if (!suspensionNoticeShown && path !== '/auth/logout') {
        suspensionNoticeShown = true;
        window.alert(body403.error || 'Your account is awaiting admin approval.');
      }
      window.location.href = '/login';
      return;
    }
  }

  // An error page from a proxy (e.g. 413 file too large, 502) isn't JSON —
  // show a plain message instead of "Unexpected token '<'" (audit 8 Oct 2026)
  let data;
  try { data = await response.json(); }
  catch (e) {
    data = { error: response.status === 413 ? 'The file is too large to upload.'
      : response.ok ? 'Unexpected reply from the server. Please try again.'
      : `The server could not complete this request (error ${response.status}). Please try again.` };
    if (response.ok) { const err = new Error(data.error); err.response = { status: response.status, data }; throw err; }
  }
  if (!response.ok) {
    const err = new Error(data.message || 'Request failed');
    err.response = { status: response.status, data };
    throw err;
  }
  return data;
}

function get(path, params)   { return apiFetch(path + toQueryString(params), { method: 'GET' }); }
function post(path, body)    { return apiFetch(path, { method: 'POST',   body: JSON.stringify(body) }); }
function put(path, body)     { return apiFetch(path, { method: 'PUT',    body: JSON.stringify(body) }); }
function del(path)           { return apiFetch(path, { method: 'DELETE' }); }

function toQueryString(params) {
  if (!params) return '';
  const usable = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!usable.length) return '';
  return '?' + usable.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
}

// For non-JSON responses (e.g. CSV export) — apiFetch always calls
// response.json(), which throws on anything that isn't valid JSON.
async function getBlob(path, params) {
  // Photos, documents, PDFs and receipts (28 Sep 2026): same automatic
  // token refresh as apiFetch — before this, a download made after the
  // 15-minute access token had expired simply failed.
  const url = `${BASE_URL}${path}${toQueryString(params)}`;
  const fetchWithTimeout = (u, opts = {}) => {
    const controller = new AbortController();
    // 2 minutes (10 Oct 2026, was 20 s): large scans / PDFs on slow mobile data
    const timer = setTimeout(() => controller.abort(), 120000);
    return fetch(u, { ...opts, signal: controller.signal }).finally(() => clearTimeout(timer));
  };
  const send = (token) => fetchWithTimeout(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });

  const token = getToken();
  let response;
  try {
    response = await send(token);
    if (token && await tokenExpired(response)) {
      const accessToken = await refreshAccessToken(fetchWithTimeout);
      if (accessToken === REFRESH_TRANSIENT) throw new Error('Connection problem — please try again in a moment.');
      if (!accessToken) {
        clearTokens();
        window.location.href = '/login';
        throw new Error('Your session has ended. Please log in again.');
      }
      response = await send(accessToken);
    }
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('Request timed out. Please try again.');
    if (e instanceof TypeError) throw new Error('Network error. Please try again.');
    throw e;
  }
  // the server's own reason, e.g. "No photo on record" (audit 9 Oct 2026)
  if (!response.ok) {
    let msg = 'Request failed';
    try { const j = await response.clone().json(); msg = j.error || j.message || msg; } catch (e) { /* not JSON */ }
    const err = new Error(msg);
    err.response = { status: response.status, data: { error: msg, message: msg } };
    throw err;
  }
  return response.blob();
}

// ─── Public (no auth — LandingPage.js, logged-out visitors) ────────────────

export const publicAPI = {
  getPricing: () => get('/public/pricing'),
  // The platform's single language list — see src/constants/languages.js.
  getLanguages: () => get('/public/languages'),
  // Per-country mobile-number rules — see src/utils/mobile.js.
  getPhoneRules: () => get('/public/phone-rules'),
  // Names / addresses on a Hindi page (13 Oct 2026): items [{ key, text }],
  // to 'hi' (English letters → Hindi letters) or 'en' (Hindi → English
  // letters). humanProof during registration; a signed-in patient needs none.
  scriptConvert: (items, to, humanProof) => post('/public/script-convert', { items, to, humanProof }),
  // Email-only launch switch (M1, 13 Oct 2026) → { mobileOtpRequired, whatsappEnabled }
  // — read through utils/authConfig.js (useAuthConfig).
  getAuthConfig: () => get('/public/auth-config'),
};

// ─── Contact Us (28 Sep 2026) — components/ContactUs.js ──────────────────
// Landing page: sendCode → verifyCode (returns { proof }) → submitPublic.
// Dashboard (patient/doctor, logged in): submit.
export const contactAPI = {
  sendCode:     (email, turnstileToken) => post('/public/contact/send-code', { email, turnstileToken }),
  verifyCode:   (email, code)           => post('/public/contact/verify-code', { email, code }),
  submitPublic: (email, proof, message) => post('/public/contact', { email, proof, message }),
  submit:       (message)               => post('/contact', { message }),
  // "Contact Us" under each questionnaire question (13 Oct 2026)
  getQuestionInfo: (condition)          => get('/contact/question', { condition }),
  sendQuestion:    (data)               => post('/contact/question', data),
  // data: { condition, questionId, questionTitle?, episodeId?, message }
  // My Messages (16 Oct 2026) — read only. folder: 'inbox' | 'sent' | 'all'.
  // Up to 2 minutes: a patient's messages are put into their language the
  // first time they are opened.
  myMessages:      (folder, page)       => apiFetch(`/contact/my${toQueryString({ folder, page })}`, { method: 'GET', timeoutMs: 120000 }),
  myUnread:        ()                   => get('/contact/my/unread'),
  myThread:        (messageId)          => apiFetch(`/contact/my/${encodeURIComponent(messageId)}`, { method: 'GET', timeoutMs: 120000 }),
};

// ─── Auth ─────────────────────────────────────────────────────────────────

export const authAPI = {
  // Token helpers — PatientPortal.js and RegisterPage.js call these as
  // authAPI.getToken() / authAPI.setTokens() / authAPI.clearTokens(), but
  // getToken/setTokens/clearTokens above were private module-level
  // functions never actually attached to authAPI. Every call site was
  // throwing "authAPI.setTokens is not a function" (login/registration
  // completion, guardian-photo flow token refresh, logout). Re-exposing
  // the existing private functions here — no behavior change, just makes
  // them reachable under the object components already call.
  getToken:       getToken,
  setTokens:      setTokens,
  clearTokens:    clearTokens,

  // Multi-step patient registration wizard
  registerStep1:  (data)                 => post('/auth/patient/register-step1', data),
  // data: { firstName, middleName, lastName, guardianName, guardianRelation,
  //         dob, dobAutoCalculated, gender, addressLine1, addressLine2,
  //         city, state, pincode, mobile, whatsapp, email, password }

  // Fired on Step 1's "Continue" click (Sep 2026 — new) so a duplicate
  // mobile/email is caught immediately, before Steps 2/3 (personal
  // details, address) are filled in for nothing. registerStep1 above
  // still re-checks at final submit regardless — this is a UX layer in
  // front of it, not a replacement.
  checkAvailability: (mobileCountryCode, mobile, email) =>
    post('/auth/patient/check-availability', { mobileCountryCode, mobile, email }),

  // Page 1 of registration (6 Oct 2026): "I am human", then codes for the
  // mobile number and email BEFORE the account exists. verify returns a
  // proof for each; registerStep1 needs both (mobileProof, emailProof).
  // data: { channel:'mobile', mobileCountryCode, mobile } | { channel:'email', email }
  patientRegHumanCheck: (turnstileToken)          => post('/auth/patient/register/human-check', { turnstileToken }),
  patientRegSendOtp:    (humanProof, data)        => post('/auth/patient/register/send-otp', { humanProof, ...data }),
  patientRegVerifyOtp:  (data, otp)               => post('/auth/patient/register/verify-otp', { ...data, otp }),

  sendVerificationOtp: (patientId, channel)      => post('/auth/patient/send-verification-otp', { patientId, channel }),
  // extra: { whatsappOptIn: true | false } for channel 'whatsapp' (28 Sep 2026)
  updateContact:       (patientId, channel, value, extra = {}) => post('/auth/patient/update-contact', { patientId, channel, value, ...extra }),
  // channel: 'mobile' | 'whatsapp' | 'email'

  verifyContactOtp:    (patientId, channel, otp) => post('/auth/patient/verify-contact-otp', { patientId, channel, otp }),

  // Anti-abuse (Aug 16 2026): wipes the in-progress registrant's data
  // after 3 consecutive wrong OTP entries on one channel. Backend refuses
  // if both mobile+email are already verified — see cancelRegistration
  // in authController.js.
  cancelRegistration:  (patientId)               => post('/auth/patient/cancel-registration', { patientId }),

  // Password reset (Sep 2026 — new). requestPasswordReset takes EITHER
  // { mobileCountryCode, mobile } OR { email } — never both — and always
  // resolves the same way regardless of whether the account exists
  // (anti-enumeration; see authController.js's own comment). The two
  // completion calls match the two request paths: OTP for mobile, a
  // token (from the emailed link's ?token= query param) for email.
  requestPasswordReset:   ({ mobileCountryCode, mobile, email, turnstileToken }) =>
    post('/auth/patient/request-password-reset', { mobileCountryCode, mobile, email, turnstileToken }),
  resetPasswordWithOtp:   ({ mobileCountryCode, mobile, otp, newPassword }) =>
    post('/auth/patient/reset-password-otp', { mobileCountryCode, mobile, otp, newPassword }),
  resetPasswordWithToken: ({ token, newPassword }) =>
    post('/auth/patient/reset-password-token', { token, newPassword }),

  // Doctor self-registration (Sep 2026 — new) — replaces the old
  // "New Doctor? [mailto support]" placeholder. registerDoctor is public
  // (no auth needed, same as patient registration); the actual approval
  // step is adminAPI.verifyDoctorAccount below, an admin-only action.
  //
  // Multipart since 24 Sep 2026 — a FormData with the text fields plus one
  // file per document type (field name = type: photo, mbbs, pg_degree,
  // superspeciality, state_council_certificate, national_council_certificate,
  // aadhaar, pan, address_proof). Built by pages/doctor/DoctorRegisterPage.js.
  // Page 1 verification (25 Sep 2026): { channel:'mobile', mobileCountryCode, mobile }
  // or { channel:'email', email }; verify adds { otp } and returns { verified, proof }.
  doctorRegSendOtp:   (data) => post('/auth/doctor/register/send-otp', data),
  doctorRegVerifyOtp: (data) => post('/auth/doctor/register/verify-otp', data),
  registerDoctor: (formData) =>
    apiFetch('/auth/doctor/register', { method: 'POST', body: formData, timeoutMs: 180000 }),

  saveConsent:    (patientId, consentType, agreed, signatureData) =>
    post('/auth/patient/consent', { patientId, consentType, agreed, signatureData }),

  savePhoto:      (patientId, photoBase64)       => post('/auth/patient/photo', { patientId, photoBase64 }),
  // photoBase64: base64-encoded JPEG/PNG from live camera capture — NOT multipart/FormData

  // REMOVED (Sep 3 2026): selectDoctor — see authController.js's REMOVED
  // note near its old location. Registration no longer has a doctor-
  // selection step; doctor choice happens per-condition via
  // conditionAPI.selectCondition instead.

  // Unified login — same endpoint for patient/doctor/admin, role tells the backend which table to check
  // turnstileToken: needed after 2 wrong passwords on the account (1 Oct 2026)
  login:          (identifier, password, role, turnstileToken) => post('/auth/login', { identifier, password, role, turnstileToken }),

  refresh:        (refreshToken)        => post('/auth/refresh', { refreshToken }),
  // Refreshes AND saves the new tokens, sharing the one-at-a-time lock
  // (8 Oct 2026 — "Continue session" used to throw the new tokens away).
  refreshSession: ()                    => refreshAccessToken(fetch).then(t => (t === REFRESH_TRANSIENT ? null : t)),
  // Password box after registration's "Go to dashboard" (8 Oct 2026)
  // refreshToken: the registration session's own, ended on success (8 Oct 2026)
  confirmPassword: (password, turnstileToken) => post('/auth/patient/confirm-password', {
    password, turnstileToken, refreshToken: sessionStorage.getItem('thyro_refresh_token') || undefined,
  }),
  logout:         (refreshToken)        => post('/auth/logout', { refreshToken }),

  // "Opinion for relative" — relative shares this account's login, no
  // separate userid/password. See authController.js migration 038.
  registerRelative: (data)              => post('/auth/relatives', data),
  // data: { firstName, middleName, lastName, relation, dob, gender }
  // relation: 'child' | 'spouse' | 'parent' | 'sibling' | 'other'

  // Minor-to-adult transition (Sep 2026 — new). Called under the
  // guardian's own login, targeting a specific relativeId — see
  // authController.isTransitionEligible for the ownership/age/not-
  // already-transitioned checks each of these independently re-runs
  // server-side, not just trusted from the UI having shown the pop-up.
  sendRelativeTransitionOtp: (data)     => post('/patient/relative/transition/send-otp', data),
  // data: { relativeId, channel: 'mobile'|'email', mobileCountryCode?, mobile?, email? }
  completeRelativeTransition: (data)    => post('/patient/relative/transition/complete', data),
  // data: { relativeId, mobileCountryCode, mobile, mobileOtp, email, emailOtp, password, consents: {treatment, telemedicine, data_privacy, terms}, signatureData }

  // Called by the now-independent patient using THEIR OWN login, not the
  // guardian's — no patientId/guardianId params, since both endpoints
  // derive the target from the caller's own formerly_managed_by_patient_id.
  grantGuardianAccess:  ()              => post('/patient/guardian-access/grant'),
  declineGuardianAccessPrompt: ()       => post('/patient/guardian-access/decline'),

  getRelatives:   ()                    => get('/auth/relatives'),

  switchProfile:  (targetPatientId)     => post('/auth/switch-profile', { targetPatientId }),
  // Returns { accessToken, refreshToken, patientId, managedBy } — caller
  // must call setTokens(accessToken, refreshToken) with the response,
  // same as after login, since this issues a fresh token pair. (setTokens
  // is already exposed above, alongside getToken/clearTokens.)
};

// ─── Patient ──────────────────────────────────────────────────────────────

export const patientAPI = {
  getProfile:     ()                    => get('/patient/profile'),
  // Convenience wrapper over updateProfile — migration 019's preferred_language
  // column. Uses the same PATCH /patient/profile endpoint; narrower call for
  // the language switcher so callers don't need to know the field name.
  updateLanguage: (preferredLanguage)   => put('/patient/profile', { preferredLanguage }),
  // WhatsApp on/off from My profile (29 Sep 2026):
  // { optIn:false } | { optIn:true, mode:'same' } | { optIn:true, mode:'other', whatsappCountryCode, whatsapp }
  updateWhatsAppPreference: (data)      => put('/patient/whatsapp-preference', data),
  // Mobile number — add / change / verify later (17 Oct 2026)
  // → { mobileCountryCode, mobile, verified, smsOn, verifyNow, canEdit }
  getMyMobile:      ()                  => get('/patient/mobile'),
  saveMyMobile:     (mobileCountryCode, mobile)      => post('/patient/mobile', { mobileCountryCode, mobile }),
  sendMyMobileCode: (mobileCountryCode, mobile)      => post('/patient/mobile/send-code', { mobileCountryCode, mobile }),
  verifyMyMobile:   (mobileCountryCode, mobile, otp) => post('/patient/mobile/verify', { mobileCountryCode, mobile, otp }),
  // Backend returns { episodes, total } — unwrap to the array here so
  // call sites (e.g. PatientDashboard.js) can use the result directly.
  getEpisodes:    (patientId)           => get(`/condition/episodes/${patientId}`).then(r => r.episodes),
  saveConsents:   (consentType, agreed, signatureData, patientId) =>
    post('/patient/consents', { consentType, agreed, signatureData, patientId }),
  // Self-service — filters by the logged-in patient's own id. Accepts
  // either a plain category string (legacy call shape, kept working for
  // existing callers) or a { category, episodeId } object — the episodeId
  // form is what the condition questionnaires use to rehydrate "already
  // uploaded" reports every time a questionnaire opens (resume, or long
  // after submit), reading from the documents table itself rather than
  // trusting a snapshot in the questionnaire's own draft JSON. (There's
  // also a separate, still-broken /patient/documents/:episodeId route
  // another caller may depend on — left alone, not touched here.)
  getDocuments:   (params) => {
    const q = typeof params === 'string' ? { category: params } : (params || undefined);
    return get('/patient/documents', q);
  },
  downloadDocument: (docId)             => getBlob(`/patient/documents/download/${docId}`),
  deleteDocument:   (docId)             => del(`/patient/documents/${docId}`),
  // Own live photo for the dashboard avatar — returns a Blob, not JSON;
  // components turn this into an object URL (see PhotoAvatar in
  // PatientPortal.js) and revoke it on unmount to avoid leaking memory.
  getPhotoBlob:   ()                    => getBlob('/patient/photo'),
  // Opinion history, invoices and blood values (the backend functions
  // getPatientOpinions / getInvoices / getBloodReportValues). Named
  // getOpinionHistory per the platform language rule ("online opinion").
  getOpinionHistory: ()                 => get('/patient/opinions'),
  getBloodValues: (params)              => get('/patient/blood-values', params),
  // Photo upload — multipart, use FormData directly (field name must be 'photo')
  // { photoConsent: true } (8 Oct 2026) — the relative/minor photo consent,
  // sent in the address so the server always receives it.
  uploadPhoto:    (formData, { photoConsent } = {}) => apiFetch(`/patient/photo${photoConsent ? '?photoConsent=true' : ''}`, {
    method: 'POST', body: formData, headers: { Authorization: `Bearer ${getToken()}` }, timeoutMs: 120000, // slow connections (8 Oct 2026)
  }),
  // REMOVED (Sep 2026) — uploadGuardianPhoto + POST /patient/guardian-photo.
  // Confirmed redundant: registerRelative now copies the parent's own
  // photo onto the new relative's record automatically at creation time,
  // since the guardian's photo was already captured moments earlier
  // during their own registration — no separate capture/upload needed.
  // Document upload — multipart
  uploadDocument: (formData)            => apiFetch('/patient/documents', {
    method: 'POST', body: formData, headers: { Authorization: `Bearer ${getToken()}` }, timeoutMs: 120000, // reports up to 5 MB on mobile data (8 Oct 2026)
  }),
  // AI auto-extract — runs an already-uploaded report through
  // documentExtractionService (Anthropic API, vision) looking for one
  // specific test's value/unit/date/reference range/lab name. Powers the
  // "🤖 Auto-fill from this report" button. See
  // patientController.extractDocumentFields.
  extractDocumentFields: (docId, testLabel) =>
    apiFetch(`/patient/documents/${docId}/extract`, { method: 'POST', body: JSON.stringify({ testLabel }), timeoutMs: 120000 }), // AI reading takes time (8 Oct 2026)
  // Parent/legal guardian consent before a minor's reports are uploaded
  // (A8, 13 Oct 2026) → { required, given }
  getGuardianUploadConsent:  ()         => get('/patient/guardian-upload-consent'),
  giveGuardianUploadConsent: ()         => post('/patient/guardian-upload-consent', { agreed: true }),
};

// ─── Conditions + Questionnaires ──────────────────────────────────────────

export const conditionAPI = {
  selectCondition:  (data)                          => post('/condition/select', data),
  // Cancel a request whose payment was never completed (6 Oct 2026)
  cancelEpisode:    (episodeId)                     => post(`/condition/episode/${episodeId}/cancel`, {}),
  // Patient's own formatted Q&A view ("My Answers") — same formatter
  // output the physician sees via physicianAPI.getEpisodeForReview,
  // scoped to the logged-in patient's own episode.
  getQuestionnaireAnswers: (episodeId)               => get(`/condition/episode/${episodeId}/questionnaire-answers`),
  // One episode of the logged-in patient (7 Oct 2026) — the questionnaire
  // window reads episode_type / followup_kind from it to know whether this
  // is an Immediate or a Routine follow-up.
  getEpisode:       (episodeId)                     => get(`/condition/episode/${episodeId}`),
  // One request, one flow (10 Oct 2026): state 'hypo' | 'hyper' | 'cancer' | null
  setPresentState:  (episodeId, state)              => post(`/condition/episode/${episodeId}/present-state`, { state }),

  // getCoreQ / saveCoreQ — RETIRED 23 Sep 2026 with CoreQuestionnaire.js.

  getHypoQ:         (patientId, episodeId)          => get(`/condition/hypo/${patientId}/${episodeId}`),
  saveHypoQ:        (patientId, episodeId, data)    => post(`/condition/hypo/${patientId}/${episodeId}`, data),

  getHyperQ:        (patientId, episodeId)          => get(`/condition/hyper/${patientId}/${episodeId}`),
  saveHyperQ:       (patientId, episodeId, data)    => post(`/condition/hyper/${patientId}/${episodeId}`, data),

  getTcQ:           (patientId, episodeId)          => get(`/condition/tc/${patientId}/${episodeId}`),
  saveTcQ:          (patientId, episodeId, data)    => post(`/condition/tc/${patientId}/${episodeId}`, data),

  getNoduleQ:       (patientId, episodeId)          => get(`/condition/nodule/${patientId}/${episodeId}`),
  saveNoduleQ:      (patientId, episodeId, data)    => post(`/condition/nodule/${patientId}/${episodeId}`, data),

  // 'other' condition (Sept 2026) — backend route, controller
  // (getOtherQuestionnaire/saveOtherQuestionnaire), table, and whitelist
  // all exist and are live; this comment used to warn they didn't.
  getOtherQ:        (patientId, episodeId)          => get(`/condition/other/${patientId}/${episodeId}`),
  saveOtherQ:       (patientId, episodeId, data)    => post(`/condition/other/${patientId}/${episodeId}`, data),
};

// ─── Opinion (patient-side) ───────────────────────────────────────────────

export const opinionAPI = {
  // Timeline
  getEpisodeTimeline:   (episodeId)       => get(`/patient/episode/${episodeId}/timeline`),

  // Read opinion
  getPatientOpinion:    (episodeId)       => get(`/patient/episode/${episodeId}/opinion`),

  // Acknowledge
  // versionNo: the opinion version the patient read (D2, migration 121) —
  // the server answers 409 OPINION_CHANGED if the doctor amended it meanwhile
  acknowledgeOpinion:   (opinionId, versionNo) => post(`/patient/opinion/${opinionId}/acknowledge`, versionNo ? { versionNo } : undefined),

  // Doctor-initiated episode messages (Sept 2026) — thread + response.
  // respondToEpisodeMessage is multipart (optional comment + optional
  // attached files), so it bypasses the JSON post() helper and calls
  // apiFetch directly with a FormData body, same pattern as
  // patientAPI.uploadDocument.
  getEpisodeMessages:      (episodeId)              => get(`/patient/episode/${episodeId}/messages`),
  respondToEpisodeMessage: (episodeId, messageId, formData) => apiFetch(
    `/patient/episode/${episodeId}/message/${messageId}/respond`,
    { method: 'POST', body: formData, headers: { Authorization: `Bearer ${getToken()}` }, timeoutMs: 120000 }
  ),

  // Investigation master (also used by OpinionWriter — doctor side)
  getInvestigationMaster: ()              => get('/physician/investigations/master'),
};

// ─── Physician (doctor-side opinion workflow) ─────────────────────────────

export const physicianAPI = {
  // Queue
  getPhysicianQueue:    ()                => get('/physician/queue'),
  getClosedEpisodes:    ()                => get('/physician/closed-episodes'), // 27 Sep 2026

  // Episode review
  getEpisodeForReview:  (episodeId)       => get(`/physician/episode/${episodeId}`),
  // Patient's live photo for identification during review — Blob, not
  // JSON (see patientAPI.getPhotoBlob for the same pattern on the
  // patient side). Backend verifies this doctor is actually assigned to
  // the patient before serving it.
  getPatientPhotoBlob:  (patientId)       => getBlob(`/physician/patient/${patientId}/photo`),

  // Documents uploaded on this episode's questionnaire (lab/imaging/
  // biopsy reports plus earlier doctors' notes/prescriptions from Additional
  // Documents) — same shape as patientAPI.getDocuments/downloadDocument,
  // just scoped to the doctor's assigned episode server-side.
  getEpisodeDocuments:  (episodeId)       => get(`/physician/episode/${episodeId}/documents`),
  // This entry's documents + every earlier entry's of the same episode,
  // with downloadedAt when this doctor already has the report (7 Oct 2026).
  getChainDocuments:    (episodeId)       => get(`/physician/episode/${episodeId}/chain-documents`),
  downloadDocument:     (docId)           => getBlob(`/physician/documents/download/${docId}`),
  // Compiled question-by-question PDF for a single episode — already
  // existed for the CURRENT episode; also used from ConditionHistoryPanel
  // (Sept 2026) to let a doctor pull up a PAST episode's own report.
  downloadQuestionnaireReport: (episodeId) => getBlob(`/physician/episode/${episodeId}/questionnaire-report`),
  // Every episode (Initial + Follow-ups) for this patient+condition,
  // newest first, capped at 20 — powers ConditionHistoryPanel's "periodic
  // status" view (Sept 2026). Scoped server-side to episodes reachable
  // from one this doctor is actually assigned to.
  getEpisodeHistory:    (episodeId)       => get(`/physician/episode/${episodeId}/history`),

  // Opinion
  saveDraftOpinion:     (episodeId, data) => post(`/physician/episode/${episodeId}/opinion/draft`, data),
  submitOpinion:        (episodeId, data) => post(`/physician/episode/${episodeId}/opinion/submit`, data),
  amendOpinion:         (opinionId, data) => put(`/physician/opinion/${opinionId}/amend`, data),
  // AI-drafted bullet-point key findings (reading aid, not a full
  // opinion draft) — re-derived server-side from the episode, no body
  // needed.
  // up to 2 minutes — the AI reads the whole case (audit 9 Oct 2026)
  getAiKeyFindings:     (episodeId)       => apiFetch(`/physician/episode/${episodeId}/opinion/ai-key-findings`, { method: 'POST', body: JSON.stringify(undefined), timeoutMs: 120000 }),

  // Translation — correct an AI translation of a patient's free-text answer
  // (migration 019). table must be one of the five questionnaire table names.
  correctFieldTranslation: (episodeId, table, field, correctedText) =>
    put(`/physician/episode/${episodeId}/questionnaire-translation`, { table, field, correctedText }),

  // Investigation master
  getInvestigationMaster: ()              => get('/physician/investigations/master'),

  // Close episode
  closeEpisode:         (episodeId)       => post(`/physician/episode/${episodeId}/close`),
  // Free follow-up recommendation on an OPEN case (valid 7 days).
  recommendFollowUp:    (episodeId, note) => post(`/physician/episode/${episodeId}/recommend-followup`, { note }),
  // Per-patient detail panel — documents, blood-value trends, private
  // notes across all of this patient's episodes with this doctor.
  getPatientOverview:   (patientId)       => get(`/physician/patient/${patientId}/overview`),
  savePatientNote:      (patientId, note) => put(`/physician/patient/${patientId}/notes`, { note }),

  // Doctor-initiated episode messages (Sept 2026)
  sendEpisodeMessage:   (episodeId, requestText) => post(`/physician/episode/${episodeId}/message`, { requestText }),
  getEpisodeMessages:   (episodeId)       => get(`/physician/episode/${episodeId}/messages`),

  // getPendingWork / investigation review / follow-up visit review — RETIRED
  // 23 Sep 2026 with the S1/S2/S3 flows (see physicianController.js).
};

// ─── Payments ─────────────────────────────────────────────────────────────

// appointmentAPI (legacy /appointment/book + verify-payment) REMOVED
// 26 Sep 2026 — no caller; every opinion payment uses paymentAPI below.

export const paymentAPI = {
  createOrder:          (data)            => post('/payment/create-order', data),
  verifyPayment:        (data)            => post('/payment/verify', data),
  // Was missing entirely — SelectDoctor.js has always called
  // paymentAPI.getDoctorFee({ doctorId, conditionType }), but no such
  // method existed here, so every call threw and was silently swallowed
  // by SelectDoctor's own .catch(() => [d.id, 'error']), showing "Fee
  // unavailable" for every doctor on the Select Doctor screen. Backend
  // route already existed and worked (paymentController.getDoctorFeeForPatient,
  // GET /payment/doctor-fee) — only this frontend mapping was missing.
  getDoctorFee:         (params)          => get('/payment/doctor-fee', params),
  // Read-only preview for the Payment screen — MRP, base fee, and standing
  // default-discount eligibility, shown before the patient clicks "Pay &
  // continue". Creates no order; the real charge is still computed fresh
  // inside createOrder above.
  getPreview:           (episodeId, params) => get(`/payment/preview/${episodeId}`, params),
  // REMOVED (per backend routes/index.js's own correction comment,
  // Sept 2 2026): /payment/followup/create-order and /payment/followup/verify
  // never existed as routes — paymentController.createOrder already handles
  // S1/S2/S3 follow-up gating itself via the `scenario` body param. These
  // two called a 404 on every use. Aliased to the real endpoints instead of
  // deleted outright, so any existing call site (data shaped
  // { episodeId, scenario, conditionType }) keeps working without a
  // separate frontend change.
};

// ─── Receipts ─────────────────────────────────────────────────────────────

export const receiptAPI = {
  // These return raw PDF bytes, not JSON — apiFetch always calls
  // response.json() on its response, which throws a SyntaxError on binary
  // PDF data. getBlob() is the correct fetch wrapper for these.
  // downloadOpinionReceipt (legacy payments table) removed 26 Sep 2026.
  downloadFollowUpReceipt: (followupPaymentId) =>
    getBlob(`/receipt/followup/${followupPaymentId}`),
  getInvoiceList: () => get('/receipt/invoices'),
};

// ─── Advise Letter ────────────────────────────────────────────────────────

export const adviseLetterAPI = {
  // Doctor
  generate:       (episodeId) => post(`/physician/episode/${episodeId}/advise-letter/generate`),
  // Authenticated download (23 Sep 2026) — opening doctorDownload's URL in
  // a new tab sends no Authorization header, so it always returned 401.
  doctorDownloadBlob: (episodeId) => getBlob(`/physician/episode/${episodeId}/advise-letter/download`),

  // Patient
  getStatus:      (episodeId) => get(`/patient/episode/${episodeId}/advise-letter/status`),
  // Through getBlob (1 Oct 2026) so an expired 15-minute login is renewed
  // automatically — the old plain-URL fetch failed after 15 minutes idle.
  patientDownloadBlob: (episodeId) => getBlob(`/patient/episode/${episodeId}/advise-letter/download`),
  // Other Problems — investigations advised, A4 PDF (27 Sep 2026)
  investigationAdviceBlob: (episodeId) => getBlob(`/patient/episode/${episodeId}/investigation-advice/pdf`),
};

// ─── Follow-up ────────────────────────────────────────────────────────────

export const followUpAPI = {
  // S1/S2/S3 methods (getStatus, missing reports, advised-investigation
  // uploads, follow-up visits) — RETIRED 23 Sep 2026 with those flows.
  // "Initiate Follow-up" (Sept 2026) — clones a completed episode's
  // questionnaire answers into a brand-new episode row server-side
  // (parent_episode_id set, episode_type='followup') and returns
  // { newEpisodeId, condition } for the frontend to hand straight to
  // AddConditionFlow's followUpEpisode prop (Payment for 'paid'; straight
  // to the questionnaire for 'doctor_recommended').
  // type: 'paid' ("Opinion sought" — case closed, goes to Payment) or
  // 'doctor_recommended' (free, within 7 days of the doctor's recommendation).
  // Episode box + follow-up chain (7 Oct 2026): type 'routine' (Scheduled
  // Follow-up — pick a doctor, pay; 'paid' is the old name and still
  // works), 'immediate' (free, within 14 days of the doctor's reply —
  // upload missing documents only) or 'doctor_recommended'.
  initiateFollowUp:     (episodeId, type = 'routine') => post(`/followup/initiate/${episodeId}`, { type }),
  // Scheduled Follow-up: the doctor chosen for it, before paying.
  setFollowUpDoctor:    (episodeId, doctorId) => post(`/followup/${episodeId}/doctor`, { doctorId }),
  // Immediate Follow-up: send the uploaded documents to the doctor.
  submitImmediateFollowUp: (episodeId)        => post(`/followup/${episodeId}/submit-immediate`, {}),
  // Documents already with the doctor from the earlier entries + this one's.
  getFollowUpDocuments: (episodeId)           => get(`/followup/${episodeId}/documents`),
};

// ─── Doctor (profile, statistics) ─────────────────────────────────────────

export const doctorAPI = {
  // → { doctors: [{ id, name, …, isAvailableToday }],
  //     availability: { today, anyAvailableToday, nextAvailable: { date, dateText, doctorCount, doctorName (only when 1) } | null } }
  listDoctors:          ()                => get('/doctors'),
  // ^ ADDED — RegisterPage.js's doctor-selection step (Step 5) calls this
  // for the public doctor list, but no matching export existed at all
  // before this fix; matches doctorAccountController.listDoctors' own comment
  // ("GET /doctors — public list for patient doctor selection"), but the
  // exact route path is a best guess pending the routes file confirming it.
  getProfile:           ()                => get('/doctor/profile'),
  getWeeklyStats:       ()                => get('/doctor/weekly-stats'),
  // "My profile" + verification documents (24 Sep 2026) — doctor-only.
  getMyProfile:         ()                => get('/doctor/me/profile'),
  updateMyProfile:      (data)            => put('/doctor/me/profile', data),
  // formData: 'docType' appended BEFORE the 'document' file.
  uploadMyDocument:     (formData)        => apiFetch('/doctor/me/documents', { method: 'POST', body: formData, timeoutMs: 120000 }),
  getMyDocumentBlob:    (docId)           => getBlob(`/doctor/me/documents/${docId}`),
  getMyPhotoBlob:       ()                => getBlob('/doctor/me/photo'),
  // Signature (28 Sep 2026) — formData: field 'signature' (JPG/PNG image,
  // or the PNG drawn on the pad). Never suspends the account.
  getMySignatureBlob:   ()                => getBlob('/doctor/me/signature/image'),
  // Bank details for fee payouts (29 Sep 2026) — dashboard only
  getMyBankDetails:     ()                => get('/doctor/me/bank-details'),
  saveMyBankDetails:    (data)            => put('/doctor/me/bank-details', data),
  uploadMySignature:    (formData)        => apiFetch('/doctor/me/signature/upload', { method: 'POST', body: formData, timeoutMs: 120000 }),
  saveMyPadSignature:   (formData)        => apiFetch('/doctor/me/signature/pad', { method: 'POST', body: formData, timeoutMs: 120000 }),
  getMyPerformance:     (period)          => get('/doctor/me/performance', { period }),
  // My availability (17 Oct 2026) — calendar of days not available.
  // save data: { weeklyOff: 'none'|'sun'|'sat_sun', holidaysOff, marks: [{ day, available }] }
  getMyAvailability:    ()                => get('/doctor/me/availability'),
  saveMyAvailability:   (data)            => put('/doctor/me/availability', data),
};

// ─── Admin ────────────────────────────────────────────────────────────────

export const adminAPI = {
  // Full doctor profile + verification documents (24 Sep 2026).
  getDoctorProfile:       (doctorId)            => get(`/admin/doctors/${doctorId}/profile`),
  updateDoctorProfile:    (doctorId, data)      => put(`/admin/doctors/${doctorId}/profile`, data),
  uploadDoctorDocument:   (doctorId, formData)  => apiFetch(`/admin/doctors/${doctorId}/documents`, { method: 'POST', body: formData, timeoutMs: 120000 }),
  getDoctorDocumentBlob:  (doctorId, docId)     => getBlob(`/admin/doctors/${doctorId}/documents/${docId}`),
  getDoctorSignatureBlob: (doctorId)            => getBlob(`/admin/doctors/${doctorId}/signature/image`), // 28 Sep 2026
  // Patient's live photo / a minor's guardian photo (which = 'guardian') — 1 Oct 2026
  getPatientPhotoBlob:    (patientId, which)    => getBlob(`/admin/patients/${patientId}/photo`, which ? { which } : undefined),
  // Per-doctor special fees (1 Oct 2026) — routes: GET/PUT /admin/doctors/:doctorId/fees[/:conditionType]
  getDoctorFees:          (doctorId)            => get(`/admin/doctors/${doctorId}/fees`),
  setDoctorFee:           (doctorId, conditionType, baseFeeRupees) => put(`/admin/doctors/${doctorId}/fees/${conditionType}`, { baseFeeRupees }),
  getDoctorBankDetails:   (doctorId)            => get(`/admin/doctors/${doctorId}/bank-details`), // 29 Sep 2026
  // Discount codes — made only by the admin (28 Sep 2026). data:
  // { code, discountAmountRupees, validFrom?, expiryDate, description?, makeDefault? } — flat rupees only
  listDiscountCodes:      ()                    => get('/admin/discount-codes'),
  createDiscountCode:     (data)                => post('/admin/discount-codes', data),
  deactivateDiscountCode: (id)                  => put(`/admin/discount-codes/${id}/deactivate`, {}),
  setDefaultDiscountCode: (id, isDefault)       => put(`/admin/discount-codes/${id}/default`, { isDefault }),
  // re-activate (optionally with new dates), change dates, delete (10 Oct 2026)
  reactivateDiscountCode: (id, dates)           => put(`/admin/discount-codes/${id}/reactivate`, dates || {}),
  updateDiscountCodeDates:(id, dates)           => put(`/admin/discount-codes/${id}/dates`, dates),
  deleteDiscountCode:     (id)                  => del(`/admin/discount-codes/${id}`),
  // Contact Us messages (28 Sep 2026); status: 'new' | 'resolved' | undefined (all)
  listContactMessages:    (params)              => get('/admin/contact-messages', params),
  resolveContactMessage:  (id, resolved)        => put(`/admin/contact-messages/${id}/resolve`, { resolved }),
  // Reply by email (ZeptoMail) and the outbox of replies sent (16 Oct 2026)
  // data: { subject?, body, markResolved? }
  replyContactMessage:    (id, data)            => post(`/admin/contact-messages/${id}/reply`, data),
  listContactReplies:     (params)              => get('/admin/contact-replies', params),
  // received + sent together; params: { source: 'patient'|'doctor'|'landing', page } (16 Oct 2026)
  listContactAll:         (params)              => get('/admin/contact-all', params),
  // Doctor verification (Sep 2026 — new) — flips a self-registered
  // doctor's is_verified flag. Matches the PUT /admin/doctor/:doctorId
  // convention setDoctorStatus already uses, just with /verify appended
  // (see routes_index.js) rather than the earlier guess this had before
  // adminController.js/routes_index.js were available to check against.
  verifyDoctorAccount: (doctorId) => put(`/admin/doctor/${doctorId}/verify`, {}),
  listPatients:         (params)          => get('/admin/patients', params),
  // Patient detail + episodes list (24 Sep 2026). The old getPatient /
  // updatePatient (/admin/patient/:id) had no backend and were removed.
  getPatientDetail:     (id)              => get(`/admin/patients/${id}`),
  // Correct date of birth / gender with a reason (A9, 13 Oct 2026)
  correctPatientIdentity: (id, data)      => put(`/admin/patients/${id}/identity`, data),
  // Delete patient / doctor (13 Oct 2026) — only records with nothing that
  // must be kept. check → { canDelete, blockers[], patientCode? }
  patientDeletionCheck: (id)              => get(`/admin/patients/${id}/deletion-check`),
  deletePatient:        (id, confirmCode, reason)  => post(`/admin/patients/${id}/delete`, { confirmCode, reason }),
  doctorDeletionCheck:  (id)              => get(`/admin/doctors/${id}/deletion-check`),
  deleteDoctor:         (id, confirmEmail, reason) => post(`/admin/doctors/${id}/delete`, { confirmEmail, reason }),
  // Per-doctor statistics; period '7' | '30' | '90' | 'all' (24 Sep 2026).
  getDoctorPerformance: (period)          => get('/admin/doctor-performance', { period }),
  listEpisodes:         (params)          => get('/admin/episodes', params),
  listDoctors:          ()                => get('/admin/doctors'),
  createDoctor:         (data)            => post('/admin/doctor', data),
  // setDoctorStatus reuses the same PUT /admin/doctor/:id route as
  // updateDoctor — the backend controller behind it (adminController.
  // setDoctorStatus) only toggles is_active from { isActive }, it doesn't
  // do a full profile update, so keep call sites using the right body shape.
  setDoctorStatus:      (id, isActive)    => put(`/admin/doctor/${id}`, { isActive }),
  getPlatformStats:     ()                => get('/admin/stats'),
  getAuditLog:          (params)          => get('/admin/audit-log', params),
  exportAuditLog:       ()                => getBlob('/admin/audit-log/export'),
  getEncryptionStatus:  ()                => get('/admin/encryption-status'),
  getPaymentReport:     (params)          => get('/admin/payments/report', params),
  // Payment alerts (M3) and refunds (M4), 13 Oct 2026
  listPaymentAlerts:    (params)          => get('/admin/payment-alerts', params),
  resolvePaymentAlert:  (id, resolution)  => put(`/admin/payment-alerts/${id}/resolve`, { resolution }),
  listRefunds:          (paymentId)       => get(`/admin/payments/${paymentId}/refunds`),
  recordRefund:         (paymentId, data) => post(`/admin/payments/${paymentId}/refunds`, data),
  // data: { amount (₹), refundedOn ('YYYY-MM-DD'), payuReference?, reason }
  getRegistrationsByCondition: (params)   => get('/admin/analytics/registrations-by-condition', params),
  getDemographics:      ()                => get('/admin/analytics/demographics'),
  // National holidays for the doctors' calendars (17 Oct 2026)
  listHolidays:         (params)          => get('/admin/holidays', params),
  saveHoliday:          (day, name)       => post('/admin/holidays', { day, name }),
  deleteHoliday:        (day)             => del(`/admin/holidays/${encodeURIComponent(day)}`),
};

// ─── Doctor handover (26 Sep 2026, migration 081) ─────────────────────────
// See handoverController.js. Used by components/HandoverPanels.js.
export const handoverAPI = {
  // Signing doctor
  requestAsDoctor:   (episodeId, data) => post(`/physician/episode/${episodeId}/handover`, data),
  cancelAsDoctor:    (handoverId)      => post(`/physician/handover/${handoverId}/cancel`, {}),
  historyForDoctor:  (episodeId)       => get(`/physician/episode/${episodeId}/handovers`),
  // Patient
  pendingForPatient: ()                => get('/patient/handovers/pending'),
  respond:           (handoverId, approve) => post(`/patient/handover/${handoverId}/respond`, { approve }),
  changeDoctor:      (episodeId, data) => post(`/patient/episode/${episodeId}/change-doctor`, data),
  historyForPatient: (episodeId)       => get(`/patient/episode/${episodeId}/handovers`),
  // Admin
  requestAsAdmin:    (episodeId, data) => post(`/admin/episodes/${episodeId}/handover`, data),
  cancelAsAdmin:     (handoverId)      => post(`/admin/handover/${handoverId}/cancel`, {}),
  historyForAdmin:   (episodeId)       => get(`/admin/episodes/${episodeId}/handovers`),
};
